import { z } from "zod";

/**
 * The Statsig gate for Sign in with Apple: the button on the sign-in page and
 * creating an account with it. Sign-in for an existing account stays open.
 *
 * Evaluated before an account exists, so it is a global on/off switch — see
 * `checkSignupGate`. Fails closed: an unrecognised gate reads false.
 */
export const APPLE_SIGNUP_GATE = "apple-signup";

/**
 * Where Sign in with Apple lives: `/options`, `/base` (the web start), the web
 * callback Apple posts to at the path itself, and `/native`.
 */
export const APPLE_SIGN_IN_PATH = "/api/p/auth/oauth/apple/signin";

/** Where the iOS app posts the credential Apple's sign-in sheet returned. */
export const APPLE_NATIVE_SIGN_IN_PATH = `${APPLE_SIGN_IN_PATH}/native`;

export type AppleSignInOptionsResp = { enabled: boolean };

/**
 * The `error` an Apple sign-up ends with when its proven email is already an
 * okthx member's sign-up address. The sign-up stops instead of minting a second
 * account, and the person is told to sign in the way they did before.
 */
export const APPLE_EMAIL_HAS_ACCOUNT = "apple_email_has_account";

/** The `error` a native Apple sign-up ends with when the waitlist holds it back. */
export const APPLE_SIGN_IN_WAITLISTED = "apple_sign_in_waitlisted";

/** What Apple posts to the web callback (`response_mode=form_post`). */
export const appleCallbackFormReq = z.object({
  state: z.string().max(4096).optional(),
  code: z.string().max(1024).optional(),
  /** JSON with the person's name, sent only on the first authorization. */
  user: z.string().max(4096).optional(),
  error: z.string().max(200).optional(),
});

/**
 * What the iOS app posts after Apple's sign-in sheet. `fullName` is present
 * only on the first authorization; Apple never sends it again.
 */
export const appleNativeSignInReq = z.object({
  identityToken: z.string().min(1).max(8192),
  authorizationCode: z.string().min(1).max(1024),
  fullName: z
    .object({
      givenName: z.string().max(200).nullish(),
      familyName: z.string().max(200).nullish(),
    })
    .nullish(),
});
export type AppleNativeSignInReq = z.infer<typeof appleNativeSignInReq>;

/** `token` is the session the app installs; `error` is a refusal code to show. */
export type AppleNativeSignInResp =
  | { status: "ok"; token: string; isNewUser: boolean }
  | { status: "refused"; error: string };
